import { defineStore } from "pinia";
import axios from "axios";
import { notificationService } from "../utils/notificationService.js";
import { toastService } from "../utils/toastService.js";
import { getDeviceTimezone } from "../utils/time.js";

axios.defaults.headers.common["X-CSRF-Token"] = document.querySelector(
  'meta[name="csrf-token"]'
).content;
axios.defaults.withCredentials = true;

axios.interceptors.response.use(
  (response) => {
    const token = response.headers["x-csrf-token"];
    if (token) {
      axios.defaults.headers.common["X-CSRF-Token"] = token;
      document.querySelector('meta[name="csrf-token"]').content = token;
    }
    return response;
  },
  (error) => {
    // Auto-handle API errors with toasts (except for specific cases)
    if (error.response && !error.config?.skipToast) {
      toastService.handleApiError(error);
    }
    return Promise.reject(error);
  }
);

function startOfLocalDay(referenceTime) {
  const dayStart = new Date(referenceTime);
  dayStart.setHours(0, 0, 0, 0);
  return dayStart;
}

function secondsWithinCurrentLocalDay(startedAt, endedAt, referenceTime = new Date()) {
  if (!startedAt || !endedAt) return 0;

  const referenceDate = new Date(referenceTime);
  const dayStart = startOfLocalDay(referenceDate).getTime();
  const startMs = new Date(startedAt).getTime();
  const endMs = new Date(endedAt).getTime();
  const referenceMs = referenceDate.getTime();
  const effectiveStart = Math.max(startMs, dayStart);
  const effectiveEnd = Math.min(endMs, referenceMs);

  return Math.max(0, Math.floor((effectiveEnd - effectiveStart) / 1000));
}

export const useBlocksStore = defineStore("blocks", {
  state: () => ({
    /**
     * @type {{
     * id: string,
     * startDate: Date,
     * endDate: Date,
     * duration: BigInteger,
     * title: string,
     * isActive: Boolean,
     * activeEventStartDate: Date | null,
     * activeEventId: string | null,
     * }[]}
     */
    blocks: [],
    /**
     * @type {{
     * id: string,
     * timer_id: string,
     * timer_name: string,
     * started_at: Date,
     * ended_at: Date,
     * duration: number,
     * }[]}
     */
    events: [],
    /**
     * @type {?number}
     * Start time in minutes from midnight
     */
    startTime: null,
    /**
     * @type {{
     * sessionId: string,
     * initializedAt: Date,
     * expired: boolean,
     * local: boolean,
     * userId: string,
     * userEmail: string,
     * userSettings: object,
     * }}
     */
    userSession: null,
    currentTime: new Date(), //
    activeModalTimerId: null,
    focusModeOpen: false,
    focusModeTimerId: null,
    showPreferencesModal: false,
    isFrontendOnly: false, // Flag to indicate frontend-only mode
    needsMigration: false,
    pendingActiveTimer: null,
    deletedBlocks: [],
    // Notification tracking state - use Map for time-based tracking
    timersDueNotified: new Map(), // Track which timers we've notified about being due (timer_id -> timestamp)
    timersCompletedNotified: new Map(), // Track which timers we've notified about completion (timer_id -> timestamp)
    // Simple session rollover detection
    sessionStale: false,
    sessionStaleReason: null,
    sessionRenewalInProgress: false,
    _sessionLastActivityCheck: 0,
    // Cross-device sync fingerprint (see syncRemoteState)
    _remoteFingerprint: null,
    _lastRemoteSyncAt: 0,
    // Projects & tags (Plus feature)
    projects: [],
    tags: [],
    // Context timer (Plus feature)
    activeContext: null,
    recentContexts: [],
    contextLoading: false,
  }),

  actions: {
    // Notification persistence helpers
    loadNotificationTracking() {
      try {
        const stored = localStorage.getItem('notification_tracking');
        if (stored) {
          const data = JSON.parse(stored);
          // Only load recent notifications (within last 24 hours)
          const cutoff = Date.now() - (24 * 60 * 60 * 1000);
          
          if (data.timersDueNotified) {
            this.timersDueNotified = new Map(
              Object.entries(data.timersDueNotified)
                .filter(([_, timestamp]) => timestamp > cutoff)
            );
          }
          
          if (data.timersCompletedNotified) {
            this.timersCompletedNotified = new Map(
              Object.entries(data.timersCompletedNotified)
                .filter(([_, timestamp]) => timestamp > cutoff)
            );
          }
        }
      } catch (error) {
        console.warn('Failed to load notification tracking:', error);
        this.timersDueNotified = new Map();
        this.timersCompletedNotified = new Map();
      }
    },

    saveNotificationTracking() {
      try {
        const data = {
          timersDueNotified: Object.fromEntries(this.timersDueNotified),
          timersCompletedNotified: Object.fromEntries(this.timersCompletedNotified)
        };
        localStorage.setItem('notification_tracking', JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to save notification tracking:', error);
      }
    },

    getLocalMidnight() {
      const now = new Date();
      const localMidnight = new Date(now);
      localMidnight.setHours(0, 0, 0, 0);
      return Math.floor(localMidnight.getTime() / 1000);
    },

    initializeStartTime() {
      if (this.isFrontendOnly && this.userSession) {
        const startDate = this.userSession.initializedAt;
        const startTime = startDate.getHours() * 60 + startDate.getMinutes();
        this.startTime = Math.floor(startTime / 60) * 60;
        this.loadBlocksFromLocalStorage();
        this.installSessionActivityListeners();
      } else if (this.userSession) {
        const startDate = this.userSession.initializedAt;
        const startTime = startDate.getHours() * 60 + startDate.getMinutes();
        this.startTime = Math.floor(startTime / 60) * 60;
        if (this.blocks.length === 0) {
          this.fetchAllTimers();
          this.fetchEvents();
          this.fetchProjectsAndTags();
          this.fetchActiveContext();
          this.fetchRecentContexts();
        }
        this.installSessionActivityListeners();
      } else {
        this.checkSession();
      }
    },

    updateCurrentTime() {
      this.currentTime = new Date();
      
      // Check for time-based notifications (online-only)
      if (!this.isFrontendOnly && this.shouldShowNotifications()) {
        this.checkTimerDueNotifications();
        this.checkTimerCompletionNotifications();
      }
    },

    startNewSession() {
      if (this.isFrontendOnly) {
        this.initLocalSession();
        
      } else {
        this.renewSession();
      }
    },

    initLocalSession() {
      try {
        this.isFrontendOnly = true;
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);
        // Fix: Calculate startTime in minutes since midnight instead of milliseconds
        this.startTime = 0; // Start at midnight (0 minutes)
        this.userSession = {
          sessionId: "local",
          initializedAt: startOfDay,
          expired: false,
          local: true,
        };
        this.blocks = [];
        this.sessionStale = false;
        this.sessionStaleReason = null;
        this.saveBlocksToLocalStorage();
        this.resetNotificationTracking();
        this.saveUserSessionToLocalStorage();
      } catch (error) {
        console.error("Error initializing local session:", error);
      }
    },

    async renewSession() {
      try {
        const localMidnight = this.getLocalMidnight();
        const { data } = await axios.post(`/api/renew_session?started_at=${localMidnight}`);
        console.log("Session renewed successfully:", data);
        const startDate = new Date(data.initialized_at);
        const startTime = startDate.getHours() * 60 + startDate.getMinutes();
        // Force change notification even when minutes-from-midnight stays the same
        this.startTime = null;
        this.startTime = Math.floor(startTime / 60) * 60;
        this.userSession = {
          sessionId: data.id,
          initializedAt: new Date(data.initialized_at),
          expired: data.expired,
          userId: data.user_id,
          userEmail: data.user_email,
          userSettings: data.user_settings || {},
          local: false,
          plan: data.plan || null,
          subscriptionStatus: data.subscription_status || null,
          entitlements: data.entitlements || [],
          todayEventSeconds: data.today_event_seconds || 0,
          todayContextSeconds: data.today_context_seconds || 0,
        };
        this.blocks = [];
        this.fetchAllTimers();
        this.fetchEvents();
        this.fetchProjectsAndTags();
        this.fetchActiveContext();
        this.fetchRecentContexts();
        this.sessionStale = false;
        this.sessionStaleReason = null;
        this.seedRemoteFingerprint();
      } catch (error) {
        console.error("Error renewing session:", error);
      }
    },

    async checkSession() {
      this.loadUserSessionFromLocalStorage();
      this.loadNotificationTracking(); // Load persisted notification state
      
      if (this.userSession) {
        console.log("Found userId session in localStorage:", this.userSession);
        this.installSessionActivityListeners();
        return;
      }

      try {
        const localMidnight = this.getLocalMidnight();
        const { data } = await axios.get(`/api/session?started_at=${localMidnight}`, {
          skipToast: true
        });
        console.log("Session fetched successfully:", data);
        const startDate = new Date(data.initialized_at);
        const startTime = startDate.getHours() * 60 + startDate.getMinutes();
        this.startTime = Math.floor(startTime / 60) * 60;
        this.userSession = {
          sessionId: data.id,
          initializedAt: new Date(data.initialized_at),
          expired: data.expired,
          userId: data.user_id,
          userEmail: data.user_email,
          userSettings: data.user_settings || {},
          local: false,
          plan: data.plan || null,
          subscriptionStatus: data.subscription_status || null,
          entitlements: data.entitlements || [],
          todayEventSeconds: data.today_event_seconds || 0,
          todayContextSeconds: data.today_context_seconds || 0,
        };
        if (data.needs_migration) {
          this.needsMigration = true;
          this.pendingActiveTimer = data.active_timer;
        }
        this.isFrontendOnly = false;
        this.deleteLocalSession();
        this.fetchAllTimers();
        this.fetchEvents();
        this.fetchProjectsAndTags();
        this.fetchActiveContext();
        this.fetchRecentContexts();
        this.installSessionActivityListeners();
        this.seedRemoteFingerprint();
      } catch (error) {
        if (error.response?.status === 404 || error.response?.status === 401) {
          this.userSession = null;
          console.log("No active session found");
        } else {
          console.error("Error checking session:", error);
        }
      }
    },

    // --- Minimal Session Rollover Detection ---
    installSessionActivityListeners() {
      // Install only once per page lifecycle
      if (this._sessionActivityInstalled) return;
      this._sessionActivityInstalled = true;

      const handler = async (evt) => {
        // Throttle checks (30s) — also acts as the debounce for cross-device sync
        const now = Date.now();
        if (now - this._sessionLastActivityCheck < 30_000) return;
        this._sessionLastActivityCheck = now;

        const sessionIdBefore = this.userSession?.sessionId;
        await this.checkIfSessionStale();

        // If the stale check renewed the session it already did a full refresh
        // (and reseeds the fingerprint), so skip the cross-device sync this round.
        if (this.sessionStale) return;
        if (this.userSession?.sessionId !== sessionIdBefore) return;

        this.syncRemoteState();
      };

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') handler('visibility');
      });
      window.addEventListener('focus', handler);
    },

    async checkIfSessionStale() {
      if (!this.userSession || this.sessionStale) return;
      if (this.sessionRenewalInProgress) return;
      try {
        const sessionDate = new Date(this.userSession.initializedAt);
        if (isNaN(sessionDate.getTime())) return;
        const sessionDayKey = sessionDate.getFullYear() + '-' + (sessionDate.getMonth()+1) + '-' + sessionDate.getDate();
        const now = new Date();
        const nowDayKey = now.getFullYear() + '-' + (now.getMonth()+1) + '-' + now.getDate();

        if (sessionDayKey === nowDayKey) return;

        if (this.isFrontendOnly) {
          this.sessionStale = true;
          this.sessionStaleReason = 'date_change';
          console.log('[session] Marked stale for frontend-only mode');
          toastService.info?.('It\'s a new day! Refresh the page to start fresh.');
          return;
        }

        // Night owl grace period for logged-in users:
        // Don't auto-renew if the user is still actively working past midnight.

        if (this.blocks.some(b => b.isActive)) {
          console.log('[session] Active timer running — skipping renewal');
          return;
        }

        if (this.blocks.some(b => b.endDate && new Date(b.endDate) > now)) {
          console.log('[session] Future blocks scheduled — skipping renewal');
          return;
        }

        const latestEndDate = this.blocks.reduce((latest, b) => {
          if (!b.endDate) return latest;
          const end = new Date(b.endDate);
          return end > latest ? end : latest;
        }, new Date(0));
        if (latestEndDate.getTime() > 0) {
          const hoursSinceLastActivity = (now.getTime() - latestEndDate.getTime()) / (1000 * 60 * 60);
          if (hoursSinceLastActivity < 1) {
            console.log('[session] Recent activity within 1h grace — skipping renewal');
            return;
          }
        }

        // All grace conditions passed — auto-renew
        this.sessionStale = true;
        this.sessionStaleReason = 'date_change';
        console.log('[session] Auto-renewing session for logged-in user');
        this.sessionRenewalInProgress = true;
        try {
          await this.renewSession();
          toastService.info?.('New day loaded — welcome back!');
        } catch (error) {
          console.error('[session] Auto-renewal failed:', error);
          this.sessionStale = false;
          this.sessionStaleReason = null;
          toastService.error?.('Session renewal failed. Please try again or reload the page.');
        } finally {
          this.sessionRenewalInProgress = false;
        }
      } catch (e) {
        console.warn('Error during stale session check', e);
      }
    },

    // --- Cross-device state sync (fingerprint-based) ---
    // On focus we fetch a cheap per-bucket fingerprint (count + max updated_at)
    // from /api/sync_status and refetch only the buckets that changed on another
    // device. No polling — piggybacks on the focus/visibility handler above.

    // In-memory only. The 5s cooldown + 30s handler throttle already dampen
    // focus-flicker refetches, so there's no value in persisting the baseline
    // across reloads (and doing so would risk trusting a stale fingerprint).
    saveRemoteFingerprint(fingerprint) {
      this._remoteFingerprint = fingerprint;
      this._lastRemoteSyncAt = Date.now();
    },

    // Fetch the current fingerprint and store it as the baseline, without refetching.
    async seedRemoteFingerprint() {
      if (this.isFrontendOnly || !this.userSession) return;
      try {
        const { data } = await axios.get('/api/sync_status', { skipToast: true });
        this.saveRemoteFingerprint(data);
      } catch (e) {
        // Non-fatal: sync falls back to seeding a baseline on the next focus.
        console.warn('Failed to seed remote fingerprint:', e);
      }
    },

    // Returns true if two {count, updated_at, ...} buckets differ.
    _bucketChanged(prev, next) {
      if (!prev || !next) return prev !== next;
      return prev.count !== next.count
        || prev.updated_at !== next.updated_at
        || prev.active_id !== next.active_id
        || prev.active_updated_at !== next.active_updated_at;
    },

    async syncRemoteState() {
      if (this.isFrontendOnly || !this.userSession) return;
      if (this.sessionRenewalInProgress) return;

      // Short cooldown guard against focus flicker / a just-seeded fingerprint.
      if (Date.now() - this._lastRemoteSyncAt < 5_000) return;

      let data;
      try {
        ({ data } = await axios.get('/api/sync_status', { skipToast: true }));
      } catch (e) {
        console.warn('Remote sync check failed:', e);
        return;
      }

      if (!data) return;

      // A different server session means ours was replaced/rolled over — re-bootstrap.
      // Plain expiry is intentionally left to checkIfSessionStale, which owns the
      // night-owl grace logic (don't renew out from under an active timer).
      if (data.session_id !== this.userSession.sessionId) {
        await this.renewSession();
        return;
      }

      const prev = this._remoteFingerprint;
      if (!prev) {
        // No baseline yet — store and skip refetch.
        this.saveRemoteFingerprint(data);
        return;
      }

      const tasks = [];
      const eventsChanged = this._bucketChanged(prev.events, data.events);
      const contextsChanged = this._bucketChanged(prev.contexts, data.contexts);

      if (this._bucketChanged(prev.timers, data.timers)) tasks.push(this.fetchAllTimers());
      if (eventsChanged) tasks.push(this.fetchEvents());
      if (contextsChanged) {
        tasks.push(this.fetchActiveContext());
        tasks.push(this.fetchRecentContexts());
      }

      if (tasks.length) {
        await Promise.all(tasks);
        // The navbar "today" totals read a server-seeded base that nothing else
        // refreshes here. Recompute only the bucket(s) that actually changed so we
        // don't downgrade an exact server total for a bucket that didn't change.
        this.refreshTodayBaseFromStore({ events: eventsChanged, contexts: contextsChanged });
      }
      this.saveRemoteFingerprint(data);
    },

    // Rebuilds the today base totals the navbar getters read (todayEventDuration /
    // todayContextDuration) from current store data, for the buckets that changed.
    // Only stopped events/contexts go into the base; the getters add live elapsed for
    // the running timer/context on top, so the running ones are skipped (no double
    // count). The server value (set on checkSession/renewSession) remains the source
    // of truth; this is a cross-device top-up that self-heals on the next bootstrap.
    refreshTodayBaseFromStore({ events = false, contexts = false } = {}) {
      if (!this.userSession) return;
      const now = new Date();

      if (events) {
        this.userSession.todayEventSeconds = this.events.reduce(
          (sum, e) => e.ended_at
            ? sum + secondsWithinCurrentLocalDay(e.started_at, e.ended_at, now)
            : sum,
          0
        );
      }

      if (contexts) {
        this.userSession.todayContextSeconds = this.recentContexts.reduce(
          (sum, c) => c.stopped_at
            ? sum + secondsWithinCurrentLocalDay(c.started_at, c.stopped_at, now)
            : sum,
          0
        );
      }
    },

    async pushLocalBlocksToRemote() {
      if (this.isFrontendOnly) return;

      this.loadBlocksFromLocalStorage();

      try {
        const localBlocks = this.blocks.filter((block) =>
          block.id.startsWith("local-")
        );
        if (!localBlocks.length) return;

        const tz = getDeviceTimezone();
        const remoteBlocks = localBlocks.map((block) => ({
          title: block.title,
          started_at: block.startDate.toISOString(),
          ended_at: block.endDate.toISOString(),
          duration: block.duration,
          active: block.isActive,
          active_event_start_date: block.activeEventStartDate,
          timezone: tz,
        }));

        const { data } = await axios.post("/api/timers/bulk-from-local", {
          timers: remoteBlocks,
        });
        console.log("Local blocks synced to remote and received:", data);

        // Check if response matches local blocks count
        if (data.timers && data.timers.length === localBlocks.length) {
          // Clear local storage
          this.blocks = this.blocks.filter(block => !block.id.startsWith("local-"));
          this.saveBlocksToLocalStorage();
          
          // Reset blocks and sync with response
          this.blocks = [];
          this.syncBlocksFromResponse(data.timers);
        }
      } catch (error) {
        console.error("Error pushing local blocks to remote:", error);
      }
    },

    async fetchAllTimers() {
      if (this.isFrontendOnly) {
        console.log("Fetching timers from localStorage");
        this.loadBlocksFromLocalStorage();
        return;
      }

      try {
        const { data } = await axios.get("/api/timers");
        console.log("Timers fetched successfully:", data);
        
        // Clear existing blocks before adding new ones
        this.blocks = [];
        
        // Map and sort the timers by start date
        this.blocks = data
          .map((timer) => ({
            id: timer.id,
            title: timer.title,
            startDate: new Date(timer.started_at),
            endDate: timer.ended_at ? new Date(timer.ended_at) : null,
            duration: timer.duration,
            isActive: !!timer.active_event,
            activeEventStartDate: timer.active_event
              ? new Date(timer.active_event.started_at)
              : null,
            activeEventId: timer.active_event?.id || null,
            project: timer.project || null,
            tags: timer.tags || [],
            notes: timer.notes ?? null,
          }))
          .sort((a, b) => a.startDate - b.startDate);
        
        // Reset notification tracking when timers are refreshed
        this.resetNotificationTracking();
      } catch (error) {
        console.error("Error fetching timers:", error);
      }
    },

    async fetchEvents() {
      if (this.isFrontendOnly) {
        console.log("Events not available in frontend-only mode");
        this.events = [];
        return;
      }

      try {
        const { data } = await axios.get("/api/events");
        console.log("Events fetched successfully:", data);
        
        // Clear existing events before adding new ones
        this.events = [];
        
        // Map and sort the events by start date
        this.events = data.events
          .filter(event => event.ended_at) // Only show completed events
          .map((event) => ({
            id: event.id,
            timer_id: event.timer_id,
            timer_name: event.timer_name,
            started_at: new Date(event.started_at),
            ended_at: new Date(event.ended_at),
            duration: event.duration,
          }))
          .sort((a, b) => a.started_at - b.started_at);
      } catch (error) {
        console.error("Error fetching events:", error);
        this.events = [];
      }
    },

    async fetchProjectsAndTags() {
      if (this.isFrontendOnly) return;

      try {
        const [projectsRes, tagsRes] = await Promise.all([
          axios.get("/api/projects"),
          axios.get("/api/tags"),
        ]);
        this.projects = projectsRes.data;
        this.tags = tagsRes.data;
      } catch (error) {
        console.error("Error fetching projects/tags:", error);
      }
    },

    async fetchActiveContext() {
      if (this.isFrontendOnly) return;

      try {
        const { data, status } = await axios.get("/api/contexts/active", { skipToast: true });
        if (status === 204) {
          this.activeContext = null;
        } else {
          this.activeContext = data;
        }
      } catch (error) {
        if (error.response?.status !== 403) {
          console.error("Error fetching active context:", error);
        }
      }
    },

    async fetchRecentContexts() {
      if (this.isFrontendOnly) return;

      try {
        const { data } = await axios.get("/api/contexts", { skipToast: true });
        this.recentContexts = Array.isArray(data) ? data : [];
      } catch (error) {
        if (error.response?.status !== 403) {
          console.error("Error fetching recent contexts:", error);
        }
      }
    },

    async searchContexts({ q = '', offset = 0, limit = 20 } = {}) {
      if (this.isFrontendOnly) return [];

      try {
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (offset) params.set('offset', offset);
        params.set('limit', limit);

        const { data } = await axios.get(`/api/contexts?${params}`, { skipToast: true });
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error searching contexts:", error);
        return [];
      }
    },

    async startContext({ projectId, projectName, tagIds, tagNames, startedAt, stoppedAt } = {}) {
      this.contextLoading = true;
      try {
        const payload = { timezone: getDeviceTimezone() };
        if (projectId) payload.project_id = projectId;
        else if (projectName) payload.project_name = projectName;
        if (tagIds?.length) payload.tag_ids = tagIds;
        if (tagNames?.length) payload.tag_names = tagNames;
        if (startedAt) payload.started_at = startedAt;
        if (stoppedAt) payload.stopped_at = stoppedAt;

        const { data } = await axios.post("/api/contexts", payload);
        const refreshRequests = [this.fetchRecentContexts()];

        // Only set as active if it's still running
        if (data.running) {
          this.activeContext = data;
        } else {
          // Refresh active context in case old one was auto-stopped
          refreshRequests.push(this.fetchActiveContext());
        }

        if (projectName || tagNames?.length) {
          refreshRequests.push(this.fetchProjectsAndTags());
        }

        await Promise.all(refreshRequests);
      } catch (error) {
        console.error("Error starting context:", error);
        throw error;
      } finally {
        this.contextLoading = false;
      }
    },

    async stopContext({ stoppedAt } = {}) {
      if (!this.activeContext) return;

      this.contextLoading = true;
      try {
        // Capture the live elapsed seconds before clearing the active context,
        // so the today summary counter doesn't jump backward on stop.
        let liveElapsed = 0;
        if (this.activeContext.started_at) {
          const referenceTime = new Date();
          const stoppedAtDate = stoppedAt ? new Date(stoppedAt) : referenceTime;
          liveElapsed = secondsWithinCurrentLocalDay(this.activeContext.started_at, stoppedAtDate, referenceTime);
        }

        const payload = { timezone: getDeviceTimezone() };
        if (stoppedAt) payload.stopped_at = stoppedAt;
        await axios.post(`/api/contexts/${this.activeContext.id}/stop`, payload);
        this.activeContext = null;

        if (liveElapsed > 0 && this.userSession) {
          this.userSession.todayContextSeconds = (this.userSession.todayContextSeconds || 0) + liveElapsed;
        }

        await this.fetchRecentContexts();
      } catch (error) {
        console.error("Error stopping context:", error);
        throw error;
      } finally {
        this.contextLoading = false;
      }
    },

    async updateContext(contextId, payload) {
      this.contextLoading = true;
      try {
        const body = { timezone: payload.timezone || getDeviceTimezone() };
        if (payload.startedAt) body.started_at = payload.startedAt;
        if (payload.stoppedAt !== undefined) body.stopped_at = payload.stoppedAt;
        if (payload.projectId !== undefined) body.project_id = payload.projectId;
        if (payload.projectName !== undefined) body.project_name = payload.projectName;
        if (payload.tagIds !== undefined) body.tag_ids = payload.tagIds;
        if (payload.tagNames !== undefined) body.tag_names = payload.tagNames;

        const { data } = await axios.patch(`/api/contexts/${contextId}`, body);

        if (this.activeContext?.id === contextId) {
          this.activeContext = data.running ? data : null;
        }

        const refreshRequests = [this.fetchRecentContexts()];
        if (payload.projectName || payload.tagNames?.length) {
          refreshRequests.push(this.fetchProjectsAndTags());
        }

        await Promise.all(refreshRequests);
        return data;
      } catch (error) {
        console.error("Error updating context:", error);
        throw error;
      } finally {
        this.contextLoading = false;
      }
    },

    async deleteContext(contextId) {
      this.contextLoading = true;
      try {
        await axios.delete(`/api/contexts/${contextId}`);
        if (this.activeContext?.id === contextId) {
          this.activeContext = null;
        }
        await this.fetchRecentContexts();
      } catch (error) {
        console.error("Error deleting context:", error);
        throw error;
      } finally {
        this.contextLoading = false;
      }
    },

    addTimer({ description, duration, projectId, projectName, tagIds, tagNames, notes }) {
      const { startDate, endDate } = this.findNextAvailableSlot(duration);
      this.createTimer(description, startDate, endDate, { projectId, projectName, tagIds, tagNames, notes });
    },

    async createTimer(title, startDate, endDate, { projectId, projectName, tagIds, tagNames, notes } = {}) {
      if (this.isFrontendOnly) {
        const newBlock = {
          id: `local-${Date.now()}`,
          title,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          duration: 0,
          isActive: false,
          activeEventStartDate: null,
          activeEventId: null,
          project: null,
          tags: [],
          notes: notes ?? null,
        };
        this.blocks.push(newBlock);
        this.saveBlocksToLocalStorage();
        return;
      }

      try {
        const payload = {
          timer: {
            title,
            started_at: startDate.toISOString(),
            ended_at: endDate.toISOString(),
          },
          timezone: getDeviceTimezone(),
        };
        if (notes !== undefined) payload.timer.notes = notes;
        if (projectId) payload.project_id = projectId;
        if (projectName) payload.project_name = projectName;
        if (tagIds?.length) payload.tag_ids = tagIds;
        if (tagNames?.length) payload.tag_names = tagNames;

        const { data } = await axios.post("/api/timers", payload);
        console.log("Timer stored successfully:", data);
        const newBlock = {
          id: data.id,
          title: data.title,
          startDate: new Date(data.started_at),
          endDate: new Date(data.ended_at),
          duration: data.duration,
          project: data.project || null,
          tags: data.tags || [],
          notes: data.notes ?? null,
        };
        this.blocks.push(newBlock);

        // Refresh projects/tags cache if new ones were created
        if (projectName || tagNames?.length) {
          this.fetchProjectsAndTags();
        }
      } catch (error) {
        console.error("Error storing timer:", error);
      }
    },

    async startTimer(id, options = {}) {
      if (this.isFrontendOnly) {
        // Stop any active timer first
        const activeBlock = this.blocks.find((b) => b.isActive);
        if (activeBlock && activeBlock.id !== id) {
          await this.stopTimer(activeBlock.id);
        }

        // Start the new timer
        const block = this.blocks.find((b) => b.id === id);
        if (block) {
          block.isActive = true;
          block.activeEventStartDate = options.startedAt ? new Date(options.startedAt) : new Date();
          this.saveBlocksToLocalStorage();
        }
        return;
      }

      // For remote timers, the backend handles stopping active timers
      await this.startTimerRemote(id, options);
    },

    async startTimerRemote(timerId, options = {}) {
      console.log("Starting timer remote with ID:", timerId, "options:", options);
      try {
        const requestBody = { timezone: getDeviceTimezone() };
        if (options.startedAt) {
          requestBody.started_at = options.startedAt;
        }

        const { data } = await axios.post(`/api/timers/${timerId}/start`, requestBody);
        console.log("Timer started successfully:", data);

        // Handle both single timer and array responses
        const timersToSync = Array.isArray(data.timers) ? data.timers : [data];
        await this.syncBlocksFromResponse(timersToSync);
        
        // Fetch updated events to include any new completed events
        this.fetchEvents();

        return { success: true, data };
      } catch (error) {
        console.error("Error starting timer:", error);
        
        // Return structured error for UI handling
        return {
          success: false,
          error: error.response?.data?.error || 'unknown_error',
          message: error.response?.data?.message || 'An error occurred',
          details: error.response?.data?.details
        };
      }
    },

    async stopTimer(id, options = {}) {
      if (this.isFrontendOnly) {
        const block = this.blocks.find((b) => b.id === id);
        if (block) {
          // Calculate elapsed time since active event started
          const endTime = options.endedAt ? new Date(options.endedAt) : new Date();
          const elapsedTime = Math.floor(
            (endTime - block.activeEventStartDate) / 1000 + 0.5
          );
          // Add elapsed time to existing duration
          block.duration = (block.duration || 0) + elapsedTime;
          block.isActive = false;
          block.activeEventStartDate = null;
          this.saveBlocksToLocalStorage();
        }
        return;
      }

      return await this.stopTimerRemote(id, options);
    },

    async stopTimerRemote(timerId, options = {}) {
      try {
        const requestBody = { timezone: getDeviceTimezone() };
        if (options.endedAt) {
          requestBody.ended_at = options.endedAt;
        }

        // Capture the live elapsed seconds before clearing the active event,
        // so the today summary counter doesn't jump backward on stop.
        const block = this.blocks.find((b) => b.id === timerId);
        let liveElapsed = 0;
        if (block?.activeEventStartDate) {
          const referenceTime = new Date();
          const endedAt = options.endedAt ? new Date(options.endedAt) : referenceTime;
          liveElapsed = secondsWithinCurrentLocalDay(block.activeEventStartDate, endedAt, referenceTime);
        }

        const { data } = await axios.post(`/api/timers/${timerId}/stop`, requestBody);
        console.log("Timer stopped successfully:", data);
        const blockIndex = this.blocks.findIndex(
          (b) => b.id === timerId
        );
        if (blockIndex !== -1) {
          this.blocks[blockIndex].isActive = false;
          this.blocks[blockIndex].activeEventStartDate = null;
          this.blocks[blockIndex].activeEventId = null;
          this.blocks[blockIndex].duration = data.duration;
        }

        // Commit the live elapsed to the session base so the navbar counter stays accurate
        if (liveElapsed > 0 && this.userSession) {
          this.userSession.todayEventSeconds = (this.userSession.todayEventSeconds || 0) + liveElapsed;
        }

        // Fetch updated events to include the newly completed event
        this.fetchEvents();

        return { success: true, data };
      } catch (error) {
        console.error("Error stopping timer:", error);

        // Return structured error for UI handling
        return {
          success: false,
          error: error.response?.data?.error || 'unknown_error',
          message: error.response?.data?.message || 'An error occurred',
          details: error.response?.data?.details
        };
      }
    },

    async discardActiveEvent(timerId) {
      // Frontend-only: simply clear active event without adding duration
      if (this.isFrontendOnly) {
        const blockIndex = this.blocks.findIndex(b => b.id === timerId);
        if (blockIndex !== -1) {
          this.blocks[blockIndex].isActive = false;
          this.blocks[blockIndex].activeEventStartDate = null;
          this.blocks[blockIndex].activeEventId = null;
          this.saveBlocksToLocalStorage();
        }
        return { success: true };
      }

      try {
        const { data } = await axios.post(`/api/timers/${timerId}/discard`);
        const blockIndex = this.blocks.findIndex(block => block.id === timerId);
        if (blockIndex !== -1) {
          this.blocks[blockIndex].isActive = false;
          this.blocks[blockIndex].activeEventStartDate = null;
          this.blocks[blockIndex].activeEventId = null;
          this.blocks[blockIndex].duration = data.duration; // Should be unchanged
        }
        // Refresh events list to reflect removal
        this.fetchEvents();
        return { success: true };
      } catch (error) {
        console.error('Error discarding active event:', error);
        return {
          success: false,
            error: error.response?.data?.error || 'discard_failed',
            message: error.response?.data?.message || 'Failed to discard active event'
        };
      }
    },

    async addCompletedEvent(timerId, options = {}) {
      // Disallow creating completed sessions in frontend-only mode
      if (this.isFrontendOnly) {
        return { success: false, error: 'frontend_only_unsupported', message: 'Completed sessions cannot be added while offline' };
      }

      try {
        const payload = {
          timer_id: timerId,
          started_at: options.startedAt,
          ended_at: options.endedAt,
          timezone: getDeviceTimezone(),
        };

        if (!payload.started_at || !payload.ended_at) {
          return { success: false, error: 'missing_params', message: 'startedAt and endedAt are required' };
        }

        const { data } = await axios.post('/api/events', payload);

        // Update timer duration and refresh events
        const updatedTimer = data.timer;
        if (updatedTimer) {
          // Sync single timer via existing sync path
          await this.syncBlocksFromResponse([updatedTimer]);
        }
        // Refresh events list to include new event
        this.fetchEvents();

        return { success: true, data };
      } catch (error) {
        console.error('Error creating completed event:', error);
        return {
          success: false,
          error: error.response?.data?.error || 'completed_event_failed',
          message: error.response?.data?.message || 'Failed to create completed event'
        };
      }
    },

    async updateTimer(id, updatedData) {
      if (this.isFrontendOnly) {
        const index = this.blocks.findIndex((block) => block.id === id);
        if (index !== -1) {
          this.blocks[index] = {
            ...this.blocks[index],
            ...updatedData,
            startDate: updatedData.startDate
              ? new Date(updatedData.startDate)
              : this.blocks[index].startDate,
            endDate: updatedData.endDate
              ? new Date(updatedData.endDate)
              : this.blocks[index].endDate,
          };
          this.saveBlocksToLocalStorage();
        }
        return;
      }

      try {
        const timerData = {};
        if (updatedData.title) timerData.title = updatedData.title;
        if (updatedData.startDate)
          timerData.started_at = updatedData.startDate.toISOString();
        if (updatedData.endDate)
          timerData.ended_at = updatedData.endDate.toISOString();
        if (updatedData.notes !== undefined) timerData.notes = updatedData.notes;

        const payload = { timer: timerData, timezone: updatedData.timezone || getDeviceTimezone() };

        // Project/tag params go at top level (not nested in timer)
        if (updatedData.projectId !== undefined) payload.project_id = updatedData.projectId;
        if (updatedData.projectName) payload.project_name = updatedData.projectName;
        if (updatedData.tagIds) payload.tag_ids = updatedData.tagIds;
        if (updatedData.tagNames) payload.tag_names = updatedData.tagNames;

        const { data } = await axios.put(`/api/timers/${id}`, payload);
        const index = this.blocks.findIndex((block) => block.id === id);
        if (index !== -1) {
          this.blocks[index] = {
            ...this.blocks[index],
            ...updatedData,
            startDate: new Date(data.started_at),
            endDate: new Date(data.ended_at),
            project: data.project || null,
            tags: data.tags || [],
            notes: data.notes ?? null,
          };
        }
      } catch (error) {
        console.error("Error updating timer:", error);
      }
    },

    async deleteTimer(id) {
      if (this.isFrontendOnly) {
        this.blocks = this.blocks.filter((block) => block.id !== id);
        // Also remove events for the deleted timer from local storage
        this.events = this.events.filter((event) => event.timer_id !== id);
        this.saveBlocksToLocalStorage();
        this.activeModalTimerId = null;
        return;
      }

      try {
        await axios.delete(`/api/timers/${id}`);
        this.blocks = this.blocks.filter((block) => block.id !== id);
        this.activeModalTimerId = null;
        // Refresh events to remove events from the deleted timer
        await this.fetchEvents();
      } catch (error) {
        console.error("Error deleting timer:", error);
      }
    },

    async restoreTimer(id) {
      try {
        const { data } = await axios.post(`/api/timers/${id}/restore`);
        const restoredTimer = {
          id: data.id,
          title: data.title,
          startDate: new Date(data.started_at),
          endDate: data.ended_at ? new Date(data.ended_at) : null,
          duration: data.duration,
          isActive: data.active,
          activeEventStartDate: data.active_event ? new Date(data.active_event.started_at) : null,
          activeEventId: data.active_event?.id || null,
          notes: data.notes ?? null,
        };
        this.blocks.push(restoredTimer);
        this.blocks.sort((a, b) => a.startDate - b.startDate);
        this.deletedBlocks = this.deletedBlocks.filter(block => block.id !== id);
      } catch (error) {
        console.error("Error restoring timer:", error);
      }
    },

    async updateEvent(eventId, payload, force = false) {
      if (this.isFrontendOnly) {
        console.log("Event editing not available in frontend-only mode");
        return { success: false, error: 'frontend_only', message: 'Event editing requires backend connection' };
      }

      try {
        const requestBody = { timezone: getDeviceTimezone(), ...payload };
        if (force) {
          requestBody.force = true;
        }

        const { data } = await axios.put(`/api/events/${eventId}`, requestBody);
        console.log("Event updated successfully:", data);
        
        // Update the timer's data if included in response
        if (data.timer) {
          const blockIndex = this.blocks.findIndex(block => block.id === data.timer.id);
          if (blockIndex !== -1) {
            this.blocks[blockIndex].duration = data.timer.duration;
            this.blocks[blockIndex].isActive = !!data.timer.active_event;
            this.blocks[blockIndex].activeEventStartDate = data.timer.active_event ? 
              new Date(data.timer.active_event.started_at) : null;
            this.blocks[blockIndex].activeEventId = data.timer.active_event?.id || null;
          }
        }
        
        // Refresh events to show updated data
        this.fetchEvents();

        return { success: true, data };
      } catch (error) {
        console.error("Error updating event:", error);
        
        // Return structured error for UI handling
        return {
          success: false,
          error: error.response?.data?.error || 'unknown_error',
          message: error.response?.data?.message || 'An error occurred',
          details: error.response?.data?.details
        };
      }
    },

    async deleteEvent(eventId) {
      if (this.isFrontendOnly) {
        console.log("Event deletion not available in frontend-only mode");
        return { success: false, error: 'frontend_only', message: 'Event deletion requires backend connection' };
      }

      try {
        const { data } = await axios.delete(`/api/events/${eventId}`);
        console.log("Event deleted successfully:", data);
        
        // Update the timer's data if included in response
        if (data.timer) {
          const blockIndex = this.blocks.findIndex(block => block.id === data.timer.id);
          if (blockIndex !== -1) {
            this.blocks[blockIndex].duration = data.timer.duration;
            this.blocks[blockIndex].isActive = !!data.timer.active_event;
            this.blocks[blockIndex].activeEventStartDate = data.timer.active_event ? 
              new Date(data.timer.active_event.started_at) : null;
            this.blocks[blockIndex].activeEventId = data.timer.active_event?.id || null;
          }
        }
        
        // Refresh events to show updated data
        this.fetchEvents();

        return { success: true, data };
      } catch (error) {
        console.error("Error deleting event:", error);
        
        // Return structured error for UI handling
        return {
          success: false,
          error: error.response?.data?.error || 'unknown_error',
          message: error.response?.data?.message || 'An error occurred',
          details: error.response?.data?.details
        };
      }
    },

    async fetchDeletedTimers() {
      try {
        const { data } = await axios.get("/api/timers/deleted");
        this.deletedBlocks = data.map(timer => ({
          id: timer.id,
          title: timer.title,
          startDate: new Date(timer.started_at),
          endDate: timer.ended_at ? new Date(timer.ended_at) : null,
          duration: timer.duration,
          isActive: false,
          deleted: true,
        }));
      } catch (error) {
        console.error("Error fetching deleted timers:", error);
      }
    },

    showModal(timerId) {
      this.activeModalTimerId = timerId;
    },

    hideModal() {
      this.activeModalTimerId = null;
    },

    openFocusMode(timerId = null) {
      this.focusModeTimerId = timerId;
      this.focusModeOpen = true;
    },

    closeFocusMode() {
      this.focusModeOpen = false;
      this.focusModeTimerId = null;
    },

    openPreferencesModal() {
      this.showPreferencesModal = true;
    },

    closePreferencesModal() {
      this.showPreferencesModal = false;
    },

    findNextAvailableSlot(durationInMinutes) {
      const roundToFive = (minutes) => Math.ceil(minutes / 5) * 5;
      const now = new Date();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const minimumStart = Math.max(this.startTime, currentMinutes);
      let nextStart = roundToFive(minimumStart);

      const sortedBlocks = this.blocks
        .slice()
        .sort((a, b) => a.startDate - b.startDate);

      for (const block of sortedBlocks) {
        const blockStartMinutes =
          block.startDate.getHours() * 60 + block.startDate.getMinutes();
        const blockEndMinutes =
          block.endDate.getHours() * 60 + block.endDate.getMinutes();

        // If we're already past this block, skip it
        if (nextStart >= blockEndMinutes) {
          continue;
        }

        // If nextStart is before this block, check if timer fits in the gap
        if (nextStart < blockStartMinutes) {
          if (nextStart + durationInMinutes <= blockStartMinutes) {
            // Timer fits before this block
            break;
          }
        }

        // Timer doesn't fit, move past this block
        nextStart = roundToFive(blockEndMinutes);
      }

      const nextStartDate = new Date();
      nextStartDate.setHours(Math.floor(nextStart / 60), nextStart % 60, 0, 0);
      const nextEndDate = new Date(
        nextStartDate.getTime() + durationInMinutes * 60 * 1000
      );

      return { startDate: nextStartDate, endDate: nextEndDate };
    },

    async syncBlocksFromResponse(timers) {
      if (!Array.isArray(timers)) {
        console.error('Expected array of timers, got:', timers);
        return;
      }
    
      try {
        // Create map of existing blocks by ID
        const existingBlocks = new Map(
          this.blocks.map(block => [block.id, block])
        );
    
        // Process incoming timers
        const syncedBlocks = timers.map(timer => {
          const block = {
            id: timer.id,
            title: timer.title,
            startDate: new Date(timer.started_at),
            endDate: new Date(timer.ended_at),
            duration: timer.duration || 0,
            isActive: false,
            activeEventStartDate: null,
            activeEventId: null,
            project: timer.project || null,
            tags: timer.tags || [],
            notes: timer.notes ?? null,
          };
    
          if (timer.active_event && timer.active_event.started_at) {
            block.isActive = true;
            block.activeEventStartDate = new Date(timer.active_event.started_at);
            block.activeEventId = timer.active_event.id;
          }
    
          // Remove from existing blocks map as it's been updated
          existingBlocks.delete(timer.id);
          return block;
        });
    
        // Combine synced blocks with remaining existing blocks
        this.blocks = [...syncedBlocks, ...existingBlocks.values()];
        console.log('Synced blocks:', this.blocks);
      } catch (error) {
        console.error('Error syncing blocks:', error);
      }
    },
    
    async signOut() {
      try {
        await axios.delete('/session', { 
          headers: {
            'Accept': 'application/json'
          }
        });
        window.location.assign('/');
      } catch (error) {
        console.error('Error signing out:', error);
        throw error;
      }
    },

    loadBlocksFromLocalStorage() {
      const storedBlocks = localStorage.getItem("blocks");
      if (storedBlocks) {
        this.blocks = JSON.parse(storedBlocks).map((block) => ({
          ...block,
          startDate: new Date(block.startDate),
          endDate: block.endDate ? new Date(block.endDate) : null,
          activeEventStartDate: block.activeEventStartDate
            ? new Date(block.activeEventStartDate)
            : null,
        }));
        
        // Reset notification tracking when blocks are loaded
        this.resetNotificationTracking();
      }
    },

    saveBlocksToLocalStorage() {
      localStorage.setItem("blocks", JSON.stringify(this.blocks));
    },

    saveUserSessionToLocalStorage() {
      try {
        const sessionData = {
          ...this.userSession,
          initializedAt: this.userSession.initializedAt.toISOString(), // Serialize Date object
        };
        localStorage.setItem("userSession", JSON.stringify(sessionData));
        console.log("User session saved to localStorage:", sessionData);
      } catch (error) {
        console.error("Error saving user session to localStorage:", error);
      }
    },

    loadUserSessionFromLocalStorage() {
      try {
        const storedSession = localStorage.getItem("userSession");
        if (storedSession) {
          const parsedSession = JSON.parse(storedSession);
          parsedSession.initializedAt = new Date(parsedSession.initializedAt); // Deserialize Date
          this.userSession = parsedSession;
          this.isFrontendOnly = true;
          console.log("User session loaded from localStorage:", parsedSession);
        }
      } catch (error) {
        console.error("Error loading user session from localStorage:", error);
      }
    },

    deleteLocalSession() {
      this.isFrontendOnly = false;
      localStorage.removeItem("userSession");
      console.log("Deleted local session from localStorage");
    },

    async migrateToNewDay() {
      if (this.pendingActiveTimer) {
        await this.stopTimer(this.pendingActiveTimer.id);
      }
      await this.renewSession();
      this.needsMigration = false;
      this.pendingActiveTimer = null;
    },

    async continueCurrentSession() {
      this.needsMigration = false;
      this.pendingActiveTimer = null;
    },

    async stopOldTimer() {
      const oldTimer = this.oldActiveTimer; // Use getter
      if (oldTimer && !this.isFrontendOnly) {
        await this.stopTimer(oldTimer.id);
      }
    },

    // Settings and Notifications Methods
    async updateUserSettings(newSettings) {
      if (this.isFrontendOnly || !this.userSession) {
        console.warn("Cannot update settings in frontend-only mode or without session");
        return;
      }

      try {
        const { data } = await axios.patch("/api/settings", {
          settings: newSettings
        });
        
        if (data.success) {
          // Update local userSession with new settings
          this.userSession.userSettings = { ...this.userSession.userSettings, ...newSettings };
          console.log("Settings updated successfully:", data.settings);
        }
      } catch (error) {
        console.error("Error updating settings:", error);
        throw error;
      }
    },

    getUserSetting(key, defaultValue = null) {
      return this.userSession?.userSettings?.[key] ?? defaultValue;
    },

    async enableNotifications() {
      if (this.isFrontendOnly) {
        console.warn('Notifications not available in frontend-only mode');
        return false;
      }
      
      if (!notificationService.supported) {
        console.warn('Notifications not supported in this browser');
        return false;
      }

      // Request permission
      const permission = await notificationService.requestPermission();
      
      if (permission === 'granted') {
        // Update user setting to enabled
        await this.updateUserSettings({
          'notifications_enabled': true
        });
        return true;
      } else {
        console.warn('Notification permission denied');
        return false;
      }
    },

    async disableNotifications() {
      if (this.isFrontendOnly) {
        console.warn('Notifications not available in frontend-only mode');
        return;
      }
      
      await this.updateUserSettings({
        'notifications_enabled': false
      });
    },

    shouldShowNotifications() {
      if (this.isFrontendOnly) return false;
      
      const userEnabled = this.getUserSetting('notifications_enabled', false);
      if (!userEnabled) return false;

      return notificationService.canShow;
    },



    // Time-based notification methods
    checkTimerDueNotifications() {
      if (this.isFrontendOnly) return;
      
      const now = this.currentTime;
      
      this.blocks.forEach(timer => {
        // Check if timer is due (current time >= start time) and we haven't notified recently
        if (timer.startDate <= now && !this.timersDueNotified.has(timer.id)) {
          // Only notify if current time is within 1 minute of scheduled start time
          const timeDifferenceMs = now.getTime() - timer.startDate.getTime();
          const timeDifferenceMinutes = timeDifferenceMs / (1000 * 60);
          
          if (timeDifferenceMinutes <= 1) {
            // Only notify if timer is not already active AND has no tracked time yet
            if (!timer.isActive && (!timer.duration || timer.duration === 0)) {
              this.showTimerDueNotification(timer);
              this.timersDueNotified.set(timer.id, Date.now());
              this.saveNotificationTracking();
            } else {
              // If timer is active or has tracked time, mark it as notified to avoid future notifications
              this.timersDueNotified.set(timer.id, Date.now());
              this.saveNotificationTracking();
            }
          } else {
            // If more than 1 minute past start time, mark as notified to avoid future notifications
            this.timersDueNotified.set(timer.id, Date.now());
            this.saveNotificationTracking();
          }
        }
      });
    },

    checkTimerCompletionNotifications() {
      if (this.isFrontendOnly) return;
      
      const now = this.currentTime;
      
      this.blocks.forEach(timer => {
        // Skip if we've already notified about completion recently
        if (this.timersCompletedNotified.has(timer.id)) return;
        
        // Check if timer should be completed
        let shouldNotifyCompletion = false;
        
        // Only check completion for active timers with proper data
        if (timer.isActive && timer.activeEventStartDate && timer.endDate) {
          const scheduledDurationMs = timer.endDate.getTime() - timer.startDate.getTime();
          const totalWorkedMs = (timer.duration || 0) * 1000; // Convert seconds to ms
          const currentSessionMs = now.getTime() - timer.activeEventStartDate.getTime();
          const totalElapsedMs = totalWorkedMs + currentSessionMs;
          
          // Timer completion: only when total elapsed time exceeds scheduled duration
          if (totalElapsedMs >= scheduledDurationMs) {
            shouldNotifyCompletion = true;
          }
        }
        
        if (shouldNotifyCompletion) {
          this.showTimerCompletionNotification(timer);
          this.timersCompletedNotified.set(timer.id, Date.now());
          this.saveNotificationTracking();
        }
      });
    },

    showTimerDueNotification(timer) {
      if (this.isFrontendOnly) return;
      
      if (this.shouldShowNotifications()) {
        notificationService.show(`Timer Due: ${timer.title || 'Untitled Timer'}`, {
          body: 'Your scheduled timer is ready to start.',
          tag: 'timer-due',
          requireInteraction: true
        });
      }
    },

    showTimerCompletionNotification(timer) {
      if (this.isFrontendOnly) return;
      
      if (this.shouldShowNotifications()) {
        notificationService.show(`Timer Completed: ${timer.title || 'Untitled Timer'}`, {
          body: 'Great work! Your timer is up.',
          tag: 'timer-completed',
          requireInteraction: true
        });
      }
    },

    // Reset notification tracking when starting new session
    resetNotificationTracking() {
      this.timersDueNotified.clear();
      this.timersCompletedNotified.clear();
      this.saveNotificationTracking();
    },

    // -- Billing actions --

    async createCheckoutSession(planKey) {
      try {
        const { data } = await axios.post("/api/checkout/session", { plan: planKey });
        if (!data?.url) {
          toastService.error("Could not start checkout. Please try again.");
          return;
        }
        window.location.href = data.url;
      } catch {
        // Axios interceptor already shows toast for API errors.
      }
    },

    async openBillingPortal() {
      try {
        const { data } = await axios.post("/api/billing/portal");
        if (!data?.url) {
          toastService.error("Could not open billing portal. Please try again.");
          return;
        }
        window.open(data.url, "_blank", "noopener,noreferrer");
      } catch {
        // Axios interceptor already shows toast for API errors.
      }
    },
  },

  getters: {
    sortedBlocks: (state) => {
      return state.blocks.slice().sort((a, b) => a.startDate - b.startDate);
    },
    
    // Combined events: completed events from API + active events from blocks
    allEvents: (state) => {
      const completedEvents = [...state.events];
      
      // Add active events from blocks
      const activeEvents = state.blocks
        .filter(block => block.isActive && block.activeEventStartDate)
        .map(block => ({
          id: `active-${block.id}`,
          timer_id: block.id,
          timer_name: block.title,
          started_at: new Date(block.activeEventStartDate),
          ended_at: null, // Active events don't have end time yet
          duration: null, // Duration calculated in real-time
        }));
      
      return [...completedEvents, ...activeEvents]
        .sort((a, b) => a.started_at - b.started_at);
    },
    
    // Detect old active timer from previous sessions
    oldActiveTimer: (state) => {
      try {
        // Skip check for local/frontend-only sessions
        if (state.isFrontendOnly || !state.userSession?.initializedAt) return null;
        
        const activeTimer = state.blocks.find(block => block.isActive);
        if (!activeTimer || !activeTimer.activeEventStartDate) return null;

        // Ensure dates are valid
        const sessionDate = new Date(state.userSession.initializedAt);
        const timerStartDate = new Date(activeTimer.startDate);
        const eventStartDate = new Date(activeTimer.activeEventStartDate);
        
        if (isNaN(sessionDate.getTime()) || isNaN(timerStartDate.getTime()) || isNaN(eventStartDate.getTime())) {
          return null;
        }

        // Check if active timer started before current session
        if (timerStartDate < sessionDate) {
          const now = new Date();
          const timeSinceStart = now.getTime() - eventStartDate.getTime();
          const hoursRunning = timeSinceStart / (1000 * 60 * 60);

          // Consider it "old" if it's been running for more than 1 hour from previous session
          if (hoursRunning > 1) {
            return activeTimer;
          }
        }
        
        return null;
      } catch (error) {
        console.warn('Error in oldActiveTimer getter:', error);
        return null;
      }
    },
    
    activeTimer: (state) =>
      state.blocks.find((block) => block.isActive) || null,
    elapsedTimeInSeconds: (state) => {
      const activeTimer = state.blocks.find((block) => block.isActive);
      if (!activeTimer || !activeTimer.activeEventStartDate) return 0;

      const elapsedTimeInSeconds = Math.max(
        0,
        Math.floor(
          (state.currentTime.getTime() -
            activeTimer.activeEventStartDate.getTime()) /
            1000 +
            1
        )
      );

      if (!activeTimer.duration) return elapsedTimeInSeconds;

      return elapsedTimeInSeconds + activeTimer.duration;
    },
    remainingTimeInSeconds: (state) => {
      const activeTimer = state.blocks.find((block) => block.isActive);
      if (!activeTimer) return 0;

      // Calculate total duration in seconds from start/end dates
      const totalDurationSeconds =
        (activeTimer.endDate.getTime() - activeTimer.startDate.getTime()) /
        1000;

      // Get current elapsed time
      const elapsedSeconds = Math.max(
        0,
        Math.floor(
          (state.currentTime.getTime() -
            activeTimer.activeEventStartDate.getTime()) /
            1000 +
            1
        )
      );

      // Add any previous duration
      const totalElapsed = elapsedSeconds + (activeTimer.duration || 0);

      // Calculate remaining time
      const remainingSeconds = totalElapsed - totalDurationSeconds;

      return remainingSeconds;
    },
    currentlyDueTimer: (state) => {
      const now = state.currentTime;
      return (
        state.blocks.find(
          (block) => block.startDate <= now && block.endDate >= now
        ) || null
      );
    },
    shouldShowMigrationPrompt: (state) => state.needsMigration,
    getPendingActiveTimer: (state) => state.pendingActiveTimer,
    nextDueTimer: (state) => {
      const now = state.currentTime;
      return state.blocks
        .filter(block => block.startDate > now)
        .sort((a, b) => a.startDate - b.startDate)[0] || null;
    },

    // -- Billing getters --
    isFreeUser: (state) => !state.userSession?.plan,
    isPaidUser: (state) => !!state.userSession?.plan,
    isPastDue: (state) => state.userSession?.subscriptionStatus === "past_due",
    hasEntitlement: (state) => (key) => {
      return (state.userSession?.entitlements || []).includes(key);
    },
    hasContextTimer() {
      return this.hasEntitlement("context_timer");
    },
    hasAnalytics() {
      return this.hasEntitlement("analytics");
    },
    hasUnlimitedHistory() {
      return this.hasEntitlement("unlimited_history");
    },
    todayEventDuration(state) {
      let base = state.userSession?.todayEventSeconds || 0;
      // Add live elapsed from running timer
      const active = this.activeTimer;
      if (active?.activeEventStartDate) {
        base += secondsWithinCurrentLocalDay(active.activeEventStartDate, state.currentTime, state.currentTime);
      }
      return base;
    },
    todayContextDuration(state) {
      let base = state.userSession?.todayContextSeconds || 0;
      // Add live elapsed from running context (backend only counts stopped contexts)
      if (state.activeContext?.started_at) {
        base += secondsWithinCurrentLocalDay(state.activeContext.started_at, state.currentTime, state.currentTime);
      }
      return base;
    },
    contextElapsedSeconds(state) {
      if (!state.activeContext?.started_at) return 0;
      return Math.max(0, Math.floor((state.currentTime.getTime() - new Date(state.activeContext.started_at).getTime()) / 1000));
    },
  },
});
